import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Receipt, Plus, X, Send, FileDown, AlertTriangle, Trash2, Upload, ExternalLink,
} from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Button } from '../../components/ui/Button'
import { ComplianceDisclaimer } from '../../components/ComplianceDisclaimer'
import { apiFetch, ApiError, getAccessToken, openPdfInNewTab } from '../../lib/api-client'
import { useSynStatus, showMoveInWarning, type SynStatus } from '../../lib/syn-status'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDKK(oere: number): string {
  return (oere / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr'
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5,
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', height: 36, padding: '0 10px',
  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#111827',
  background: '#fff', fontFamily: 'inherit', outline: 'none',
}

const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

// ─── Types (mirror backend wire shape) ───────────────────────────────────────

type DeductionType = 'normalistandsættelse' | 'misligholdelse'

interface DeductionLine {
  type: DeductionType
  description: string
  amount_oere: number
  bilag_id: string | null
  bilag_filename?: string | null
}

interface Settlement {
  id: string
  contract_id: string
  lease_id: string
  lease_address?: string
  unit_no?: string
  property_address?: string
  renter_name?: string | null
  renter_email?: string | null
  deposit_oere: number
  forudbetalt_leje_oere: number
  restance_oere: number
  forbrug_hold_back_oere: number
  deduction_lines: DeductionLine[]
  netto_oere: number
  outcome: 'refund' | 'surcharge'
  status: 'draft' | 'delivered'
  outcome_invoice_id?: string | null
  fraflytning_dato: string
  delivered_at?: string | null
  created_at: string
}

interface OpsagtContract {
  id: string
  lease_id: string
  type: string
  monthly_rent_oere: number
  deposit_oere: number
  prepaid_rent_oere: number
  start_date: string
  status: string
  opsagt_dato: string | null
  fraflytning_dato: string | null
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, variant, onClose }: { message: string; variant?: 'success' | 'error'; onClose: () => void }) {
  React.useEffect(() => {
    const t = setTimeout(onClose, 5000)
    return () => clearTimeout(t)
  }, [onClose])
  const bg = variant === 'error' ? '#dc2626' : '#16a34a'
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 999,
      background: bg, color: '#fff', borderRadius: 8,
      padding: '12px 20px', fontSize: 13, fontWeight: 500,
      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
      display: 'flex', alignItems: 'flex-start', gap: 12, maxWidth: 460,
    }}>
      <span style={{ flex: 1, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: 0, marginTop: 2 }}>
        <X size={14} />
      </button>
    </div>
  )
}

// ─── Status / outcome chips ──────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
  const styles: Record<string, React.CSSProperties> = {
    draft: { background: '#f3f4f6', color: '#6b7280', border: '1px solid #e8eaed' },
    delivered: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
  }
  const labels: Record<string, string> = { draft: 'Kladde', delivered: 'Afleveret' }
  const s = styles[status] ?? styles.draft
  return (
    <span style={{ ...s, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 500 }}>
      {labels[status] ?? status}
    </span>
  )
}

function OutcomeAmount({ outcome, netto_oere }: { outcome: 'refund' | 'surcharge'; netto_oere: number }) {
  if (outcome === 'refund') {
    return (
      <span style={{ color: '#16a34a', fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>
        +{formatDKK(netto_oere)}
      </span>
    )
  }
  return (
    <span style={{ color: '#dc2626', fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>
      −{formatDKK(Math.abs(netto_oere))}
    </span>
  )
}

// ─── Bilag upload (re-uses the contract_bilag endpoint) ──────────────────────

async function uploadBilag(contractId: string, file: File): Promise<{ id: string; filename: string }> {
  const token = getAccessToken()
  const form = new FormData()
  form.append('file', file)
  const resp = await fetch(`${apiUrl}/api/v1/contracts/${contractId}/bilag`, {
    method: 'POST',
    body: form,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!resp.ok) {
    const j: { detail?: string } = await resp.json().catch(() => ({}))
    throw new Error(j.detail ?? `Upload mislykkedes (${resp.status})`)
  }
  return resp.json() as Promise<{ id: string; filename: string }>
}

// ─── Deduction line editor (used in wizard step 2) ───────────────────────────

function DeductionLineRow({
  line, contractId, onChange, onRemove, onError,
}: {
  line: DeductionLine
  contractId: string
  onChange: (next: DeductionLine) => void
  onRemove: () => void
  onError: (msg: string) => void
}) {
  const [uploading, setUploading] = React.useState(false)
  const fileRef = React.useRef<HTMLInputElement>(null)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    setUploading(true)
    try {
      const res = await uploadBilag(contractId, f)
      onChange({ ...line, bilag_id: res.id, bilag_filename: res.filename })
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'Upload mislykkedes')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const amountDKK = (line.amount_oere / 100).toFixed(2).replace('.', ',')

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '160px 1fr 110px auto',
      gap: 8, alignItems: 'start', padding: '10px 0', borderBottom: '1px solid #f3f4f6',
    }}>
      <select
        style={inputStyle}
        value={line.type}
        onChange={e => onChange({ ...line, type: e.target.value as DeductionType })}
      >
        <option value="normalistandsættelse">Normalistandsættelse</option>
        <option value="misligholdelse">Misligholdelse</option>
      </select>
      <div>
        <input
          style={inputStyle}
          placeholder="Beskrivelse (fx maling stue)"
          value={line.description}
          onChange={e => onChange({ ...line, description: e.target.value })}
        />
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={handleFile}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{
              padding: '4px 10px', borderRadius: 5, fontSize: 11, fontWeight: 500,
              background: line.bilag_id ? '#f0fdf4' : '#fff',
              color: line.bilag_id ? '#16a34a' : '#6b7280',
              border: `1px solid ${line.bilag_id ? '#bbf7d0' : '#e8eaed'}`,
              cursor: uploading ? 'not-allowed' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <Upload size={11} />
            {uploading ? 'Uploader…' : line.bilag_id ? (line.bilag_filename ?? 'Bilag uploadet') : 'Vedhæft faktura/tilbud'}
          </button>
          {!line.bilag_id && (
            <span style={{ fontSize: 11, color: '#9ca3af' }}>
              Påkrævet — uden bilag kan opgørelsen ikke afleveres
            </span>
          )}
        </div>
      </div>
      <input
        style={{ ...inputStyle, textAlign: 'right', fontFamily: 'monospace' }}
        type="text"
        inputMode="decimal"
        placeholder="0,00"
        value={amountDKK === '0,00' ? '' : amountDKK}
        onChange={e => {
          const raw = e.target.value.replace(/\./g, '').replace(',', '.')
          const num = parseFloat(raw)
          onChange({ ...line, amount_oere: isNaN(num) ? 0 : Math.round(num * 100) })
        }}
      />
      <button
        onClick={onRemove}
        type="button"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 8 }}
        title="Fjern linje"
      >
        <Trash2 size={14} />
      </button>
    </div>
  )
}

// ─── Creation wizard ──────────────────────────────────────────────────────────

interface WizardProps {
  contracts: OpsagtContract[]
  initialContractId: string | null
  onClose: () => void
  onCreated: (s: Settlement) => void
  onDelivered: (s: Settlement) => void
}

function CreationWizard({ contracts, initialContractId, onClose, onCreated, onDelivered }: WizardProps) {
  const [step, setStep] = React.useState<1 | 2 | 3>(initialContractId ? 2 : 1)
  const [contractId, setContractId] = React.useState(initialContractId ?? '')
  const [lines, setLines] = React.useState<DeductionLine[]>([])
  const [forbrugHoldBackDKK, setForbrugHoldBackDKK] = React.useState('')
  const [creating, setCreating] = React.useState(false)
  const [createError, setCreateError] = React.useState<string | null>(null)
  const [draft, setDraft] = React.useState<Settlement | null>(null)
  const [delivering, setDelivering] = React.useState(false)
  const [deliverError, setDeliverError] = React.useState<string | null>(null)

  const selectedContract = contracts.find(c => c.id === contractId) ?? null
  const synStatus = useSynStatus(step >= 2 ? selectedContract?.id : null)
  const forbrugHoldBackOere = Math.round((parseFloat(forbrugHoldBackDKK.replace(/\./g, '').replace(',', '.')) || 0) * 100)
  const deductionsSum = lines.reduce((s, l) => s + l.amount_oere, 0)
  const depositOere = selectedContract?.deposit_oere ?? 0
  const forudbetaltOere = selectedContract?.prepaid_rent_oere ?? 0
  // Preview netto excludes restance (server-pulled at create time)
  const nettoPreviewOere = depositOere - deductionsSum - forbrugHoldBackOere
  const missingBilag = lines.filter(l => !l.bilag_id).length
  const incompleteLines = lines.filter(l => l.amount_oere <= 0 || !l.bilag_id).length

  function addLine() {
    setLines(prev => [
      ...prev,
      { type: 'normalistandsættelse', description: '', amount_oere: 0, bilag_id: null },
    ])
  }

  function updateLine(i: number, next: DeductionLine) {
    setLines(prev => prev.map((l, idx) => (idx === i ? next : l)))
  }

  function removeLine(i: number) {
    setLines(prev => prev.filter((_, idx) => idx !== i))
  }

  async function createDraft() {
    if (!contractId) return
    setCreating(true)
    setCreateError(null)
    try {
      const s = await apiFetch<Settlement>('/api/v1/fraflytning', {
        method: 'POST',
        body: JSON.stringify({
          contract_id: contractId,
          deduction_lines: lines.map(l => ({
            type: l.type,
            description: l.description,
            amount_oere: l.amount_oere,
            bilag_id: l.bilag_id,
          })),
          forbrug_hold_back_oere: forbrugHoldBackOere,
        }),
      })
      setDraft(s)
      onCreated(s)
      setStep(3)
    } catch (e: unknown) {
      setCreateError(e instanceof Error ? e.message : 'Kunne ikke oprette kladde.')
    } finally {
      setCreating(false)
    }
  }

  async function deliver() {
    if (!draft) return
    setDelivering(true)
    setDeliverError(null)
    try {
      await apiFetch<{ ok: boolean; outcome: string; status: string }>(
        `/api/v1/fraflytning/${draft.id}/deliver`,
        { method: 'POST' },
      )
      onDelivered({ ...draft, status: 'delivered' })
      onClose()
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 422) {
        setDeliverError(e.message)
      } else {
        setDeliverError(e instanceof Error ? e.message : 'Kunne ikke aflevere opgørelsen.')
      }
    } finally {
      setDelivering(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: '#fff', borderRadius: 10, width: '100%', maxWidth: 720,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px', borderBottom: '1px solid #e8eaed',
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Ny fraflytningsopgørelse</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {step === 1 ? 'Trin 1 af 3 — Vælg opsagt lejeforhold'
                : step === 2 ? 'Trin 2 af 3 — Fradrag fra depositum'
                  : 'Trin 3 af 3 — Gennemse og aflever'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: 24, flex: 1 }}>

          {step === 1 && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Opsagt lejeforhold</label>
                <select style={inputStyle} value={contractId} onChange={e => setContractId(e.target.value)}>
                  <option value="">— Vælg lejeforhold —</option>
                  {contracts.map(c => (
                    <option key={c.id} value={c.id}>
                      Kontrakt {c.id.slice(0, 8)} — fraflytning {fmtDate(c.fraflytning_dato)}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', background: '#f9fafb', border: '1px solid #e8eaed', borderRadius: 6, padding: '10px 14px' }}>
                Kun lejeforhold der er opsagt (med fraflytningsdato) vises. Mangler et lejeforhold, skal opsigelsen først registreres fra lejeforholdsiden.
              </div>
              {contracts.length === 0 && (
                <div style={{ marginTop: 16, padding: 14, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, color: '#92400e', fontSize: 13 }}>
                  Der er ingen opsagte lejeforhold endnu.
                </div>
              )}
            </>
          )}

          {step === 2 && selectedContract && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                <SummaryStat label="Depositum" value={formatDKK(depositOere)} />
                <SummaryStat label="Forudbetalt leje" value={formatDKK(forudbetaltOere)} hint="informativt — ikke i pot" />
                <SummaryStat label="Fraflytningsdato" value={fmtDate(selectedContract.fraflytning_dato)} />
              </div>

              {synStatus?.applies && <SynStatusPanel status={synStatus} />}

              <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Fradragslinjer (istandsættelse / misligholdelse)</div>
                <Button size="sm" variant="secondary" onClick={addLine}>
                  <Plus size={12} /> Tilføj linje
                </Button>
              </div>

              {lines.length === 0 && (
                <div style={{ padding: 16, background: '#f9fafb', borderRadius: 6, fontSize: 12, color: '#6b7280', border: '1px dashed #e8eaed', marginBottom: 16 }}>
                  Ingen fradragslinjer — depositum udbetales fuldt ud minus eventuel restance.
                </div>
              )}

              {lines.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '160px 1fr 110px 30px',
                    gap: 8, padding: '6px 0', borderBottom: '1px solid #e8eaed',
                    fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
                  }}>
                    <div>Type</div>
                    <div>Beskrivelse + bilag</div>
                    <div style={{ textAlign: 'right' }}>Beløb (kr)</div>
                    <div />
                  </div>
                  {lines.map((l, i) => (
                    <DeductionLineRow
                      key={i}
                      line={l}
                      contractId={selectedContract.id}
                      onChange={(next) => updateLine(i, next)}
                      onRemove={() => removeLine(i)}
                      onError={(msg) => setCreateError(msg)}
                    />
                  ))}
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Tilbageholdt til forbrugsregnskab (kr)</label>
                <input
                  style={{ ...inputStyle, maxWidth: 180 }}
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={forbrugHoldBackDKK}
                  onChange={e => setForbrugHoldBackDKK(e.target.value)}
                />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  Rimelig andel af depositum kan tilbageholdes indtil forbrugsregnskab foreligger.
                </div>
              </div>

              <div style={{ background: '#f9fafb', borderRadius: 8, padding: 14, border: '1px solid #e8eaed', fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: '#6b7280', marginBottom: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Forhåndsvisning af netto (eksklusiv restance)
                </div>
                <Row k="Depositum" v={formatDKK(depositOere)} />
                <Row k={`Fradrag i alt (${lines.length} ${lines.length === 1 ? 'linje' : 'linjer'})`} v={`−${formatDKK(deductionsSum)}`} />
                <Row k="Tilbageholdt til forbrug" v={`−${formatDKK(forbrugHoldBackOere)}`} />
                <Row k="Restance" v="beregnes ved gemning" mute />
                <div style={{ borderTop: '1px solid #e8eaed', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                  <span style={{ color: '#111827' }}>Foreløbig netto</span>
                  <span style={{ fontFamily: 'monospace', color: nettoPreviewOere >= 0 ? '#16a34a' : '#dc2626' }}>
                    {nettoPreviewOere >= 0 ? '+' : '−'}{formatDKK(Math.abs(nettoPreviewOere))}
                  </span>
                </div>
              </div>

              {(missingBilag > 0 || incompleteLines > 0) && (
                <div style={{ marginTop: 12, padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, color: '#92400e', fontSize: 12 }}>
                  <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {missingBilag} linje{missingBilag === 1 ? '' : 'r'} mangler dokumentation eller beløb. Opgørelsen kan ikke afleveres uden bilag.
                </div>
              )}

              {createError && (
                <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef2f2', color: '#dc2626', fontSize: 13, borderRadius: 6, border: '1px solid #fecaca', whiteSpace: 'pre-wrap' }}>
                  {createError}
                </div>
              )}
            </>
          )}

          {step === 3 && draft && (
            <>
              <div style={{ border: '1px solid #e8eaed', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                {([
                  ['Depositum', formatDKK(draft.deposit_oere)],
                  ['Forudbetalt leje (informativt)', formatDKK(draft.forudbetalt_leje_oere)],
                  ['Restance (auto-trukket)', `−${formatDKK(draft.restance_oere)}`],
                  ['Fradrag i alt', `−${formatDKK(draft.deduction_lines.reduce((s, l) => s + l.amount_oere, 0))}`],
                  ['Tilbageholdt til forbrug', `−${formatDKK(draft.forbrug_hold_back_oere)}`],
                ] as [string, string][]).map(([k, v], i) => (
                  <div key={k} style={{
                    display: 'flex', justifyContent: 'space-between', padding: '9px 14px',
                    background: i % 2 === 0 ? '#f9fafb' : '#fff',
                    borderBottom: '1px solid #e8eaed', fontSize: 13,
                  }}>
                    <span style={{ color: '#6b7280' }}>{k}</span>
                    <span style={{ color: '#111827', fontWeight: 500, fontFamily: 'monospace' }}>{v}</span>
                  </div>
                ))}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', padding: '12px 14px',
                  background: draft.outcome === 'refund' ? '#f0fdf4' : '#fef2f2',
                }}>
                  <span style={{ fontWeight: 700, color: '#111827', fontSize: 13 }}>
                    {draft.outcome === 'refund' ? 'Til godskrivning' : 'Efterbetaling'}
                  </span>
                  <span style={{
                    fontWeight: 700, fontFamily: 'monospace', fontSize: 13,
                    color: draft.outcome === 'refund' ? '#16a34a' : '#dc2626',
                  }}>
                    {draft.outcome === 'refund' ? '+' : '−'}{formatDKK(Math.abs(draft.netto_oere))}
                  </span>
                </div>
              </div>

              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                "Aflever opgørelse" {draft.outcome === 'surcharge' ? 'opretter en efterregulerings-faktura (deposit_claim) og ' : ''}
                låser kontrakten og afslutter lejeforholdet. PDF kan hentes fra listen herefter.
              </div>

              {deliverError && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#fef2f2', color: '#dc2626', fontSize: 13, borderRadius: 6, border: '1px solid #fecaca', whiteSpace: 'pre-wrap' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Opgørelsen kan ikke afleveres:</div>
                  {deliverError}
                </div>
              )}
            </>
          )}

          {step >= 2 && <ComplianceDisclaimer />}
        </div>

        <div style={{
          padding: '14px 24px', borderTop: '1px solid #e8eaed',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <div>
            {step > 1 && step < 3 && !initialContractId && (
              <Button variant="secondary" size="sm" onClick={() => setStep(prev => (prev - 1) as 1 | 2 | 3)}>
                ← Tilbage
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" onClick={onClose}>Annuller</Button>
            {step === 1 && (
              <Button size="sm" disabled={!contractId} onClick={() => setStep(2)}>Næste →</Button>
            )}
            {step === 2 && (
              <Button size="sm" isLoading={creating} disabled={!contractId} onClick={createDraft}>
                Opret kladde →
              </Button>
            )}
            {step === 3 && draft && (
              <Button size="sm" isLoading={delivering} onClick={deliver}>
                <Send size={13} /> Aflever opgørelse
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Syn-status panel ────────────────────────────────────────────────────────

type SynTone = 'ok' | 'warn' | 'bad' | 'none'
const SYN_TONE_COLOR: Record<SynTone, string> = {
  ok: '#16a34a', warn: '#d97706', bad: '#dc2626', none: '#d1d5db',
}

function SynLine({ label, value, tone, status }: {
  label: string; value?: string; tone: SynTone; status: string
}) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) auto minmax(180px, 1.3fr)',
      gap: 12, alignItems: 'baseline', padding: '6px 0', fontSize: 12,
    }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ color: '#111827', fontWeight: 500, whiteSpace: 'nowrap' }}>{value ?? ''}</span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, color: '#374151' }}>
        <span aria-hidden style={{
          width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
          background: SYN_TONE_COLOR[tone], display: 'inline-block',
        }} />
        {status}
      </span>
    </div>
  )
}

function SynGroupTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase',
      letterSpacing: '0.05em', margin: '10px 0 2px',
    }}>
      {children}
    </div>
  )
}

function SynStatusPanel({ status }: { status: SynStatus }) {
  const { move_in: mi, move_out: mo, rules } = status
  const moveInBad = showMoveInWarning(mi)
  const moveInLine: { tone: SynTone; text: string } = moveInBad
    ? { tone: 'bad', text: 'Ikke afholdt rettidigt' }
    : mi.held_date
      ? { tone: 'ok', text: 'Afholdt rettidigt' }
      : { tone: 'none', text: 'Ikke afholdt endnu' }

  const noticeLine: { tone: SynTone; text: string } =
    mo.notice_ok === true ? { tone: 'ok', text: `Varsel overholdt (mindst ${rules.notice_minimum_days} dage)` }
      : mo.notice_ok === false ? { tone: 'bad', text: `Varsel for kort (mindst ${rules.notice_minimum_days} dage)` }
        : { tone: 'none', text: 'Ikke oplyst' }
  const synLine: { tone: SynTone; text: string } =
    mo.syn_within_deadline === true ? { tone: 'ok', text: 'Afholdt inden fristen' }
      : mo.syn_within_deadline === false ? { tone: 'bad', text: 'Ikke afholdt inden fristen' }
        : { tone: 'none', text: 'Ikke afholdt endnu' }
  const rapportLine: { tone: SynTone; text: string } =
    mo.rapport_within_deadline === true ? { tone: 'ok', text: `Udleveret rettidigt (senest ${rules.report_max_days_after_syn} dage efter syn)` }
      : mo.rapport_within_deadline === false ? { tone: 'bad', text: `Ikke udleveret rettidigt (senest ${rules.report_max_days_after_syn} dage efter syn)` }
        : { tone: 'none', text: 'Ikke udleveret endnu' }

  const allowed = status.istandsaettelse_allowed
  return (
    <div style={{ border: '1px solid #e8eaed', borderRadius: 8, padding: '12px 14px', marginBottom: 20, background: '#fff' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Syn-status</div>

      <SynGroupTitle>Indflytningssyn (senest {rules.move_in_deadline_days} dage efter indflytning)</SynGroupTitle>
      <SynLine label="Frist" value={fmtDate(mi.deadline)} tone={moveInLine.tone} status={moveInLine.text} />
      <SynLine label="Afholdt" value={fmtDate(mi.held_date)} tone="none" status={mi.held_date ? 'Registreret' : 'Ikke registreret'} />

      <SynGroupTitle>Fraflytningssyn (senest {rules.move_out_deadline_days} dage efter fraflytning)</SynGroupTitle>
      <SynLine label="Frist for syn" value={fmtDate(mo.syn_deadline)} tone={synLine.tone} status={synLine.text} />
      <SynLine label="Indkaldelse sendt" value={fmtDate(mo.syn_indkaldt_dato)} tone={noticeLine.tone} status={noticeLine.text} />
      <SynLine label="Syn afholdt" value={fmtDate(mo.syn_afholdt_dato)} tone="none" status={mo.syn_afholdt_dato ? 'Registreret' : 'Ikke registreret'} />
      <SynLine label="Rapport udleveret" value={fmtDate(mo.rapport_leveret_dato)} tone={rapportLine.tone} status={rapportLine.text} />

      {allowed !== null && (
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: '1px solid #e8eaed',
          display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 13, fontWeight: 600, color: '#111827',
        }}>
          <span aria-hidden style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
            background: allowed ? SYN_TONE_COLOR.ok : SYN_TONE_COLOR.bad,
          }} />
          {allowed ? 'Istandsættelse kan fratrækkes depositum' : 'Istandsættelse kan ikke fratrækkes depositum'}
        </div>
      )}
      {status.issues.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
          {status.issues.map((issue, i) => <li key={i}>{issue}</li>)}
        </ul>
      )}
    </div>
  )
}

// ─── Tiny presentational helpers ─────────────────────────────────────────────

function SummaryStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ padding: 12, background: '#f9fafb', border: '1px solid #e8eaed', borderRadius: 8 }}>
      <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#111827', fontWeight: 600, fontFamily: 'monospace' }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{hint}</div>}
    </div>
  )
}

function Row({ k, v, mute }: { k: string; v: string; mute?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
      <span style={{ color: '#6b7280' }}>{k}</span>
      <span style={{ fontFamily: 'monospace', color: mute ? '#9ca3af' : '#111827' }}>{v}</span>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main page
// ═══════════════════════════════════════════════════════════════════════════════

export function FraflytningsopgoerelsePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialContractParam = searchParams.get('contract')

  const [items, setItems] = React.useState<Settlement[]>([])
  const [contracts, setContracts] = React.useState<OpsagtContract[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [statusFilter, setStatusFilter] = React.useState<string>('')
  const [showWizard, setShowWizard] = React.useState(false)
  const [toast, setToast] = React.useState<{ message: string; variant?: 'success' | 'error' } | null>(null)
  const [delivering, setDelivering] = React.useState<string | null>(null)
  const [deletingId, setDeletingId] = React.useState<string | null>(null)

  const fetchItems = React.useCallback(() => {
    setLoading(true)
    apiFetch<{ items: Settlement[]; total: number }>('/api/v1/fraflytning')
      .then(r => setItems(r.items))
      .catch(() => setError('Kunne ikke hente fraflytningsopgørelser.'))
      .finally(() => setLoading(false))
  }, [])

  const fetchContracts = React.useCallback(() => {
    apiFetch<OpsagtContract[]>('/api/v1/contracts')
      .then(rows => setContracts(rows.filter(c => c.opsagt_dato && c.fraflytning_dato && c.status !== 'terminated')))
      .catch(() => {})
  }, [])

  // One-shot mount-time data load.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  React.useEffect(() => { fetchItems() }, [fetchItems])
  React.useEffect(() => { fetchContracts() }, [fetchContracts])

  // Deep-link from LejeforholdDetailPage: ?contract=<id> auto-opens wizard.
  // Reacts to the URL search param (can change without remount via history
  // nav), not a one-time init; deriving at render would miss later param changes.
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (initialContractParam) setShowWizard(true)
  }, [initialContractParam])

  const filtered = statusFilter ? items.filter(s => s.status === statusFilter) : items

  async function downloadPdf(id: string) {
    try {
      await openPdfInNewTab(`/api/v1/fraflytning/${id}/pdf`)
    } catch {
      alert('Kunne ikke hente PDF.')
    }
  }

  async function deliverItem(s: Settlement) {
    setDelivering(s.id)
    try {
      await apiFetch(`/api/v1/fraflytning/${s.id}/deliver`, { method: 'POST' })
      setToast({ message: `Opgørelsen er afleveret (${s.outcome === 'refund' ? 'til godskrivning' : 'efterbetaling'}).` })
      fetchItems()
    } catch (e: unknown) {
      const msg = e instanceof ApiError && e.status === 422
        ? `Opgørelsen kan ikke afleveres:\n${e.message}`
        : e instanceof Error ? e.message : 'Afleveringen mislykkedes.'
      setToast({ message: msg, variant: 'error' })
    } finally {
      setDelivering(null)
    }
  }

  async function deleteDraft(s: Settlement) {
    if (!window.confirm('Slet kladde? Dette kan ikke fortrydes.')) return
    setDeletingId(s.id)
    try {
      await apiFetch(`/api/v1/fraflytning/${s.id}`, { method: 'DELETE' })
      setToast({ message: 'Kladden er slettet.' })
      fetchItems()
    } catch (e: unknown) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 409)) fetchItems()
      const msg = e instanceof ApiError && e.status === 404
        ? 'Kladden findes ikke længere.'
        : e instanceof Error ? e.message : 'Kladden kunne ikke slettes.'
      setToast({ message: msg, variant: 'error' })
    } finally {
      setDeletingId(null)
    }
  }

  function closeWizard() {
    setShowWizard(false)
    if (initialContractParam) {
      const next = new URLSearchParams(searchParams)
      next.delete('contract')
      setSearchParams(next, { replace: true })
    }
  }

  return (
    <AppLayout>
      <div style={{ padding: '0 0 24px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 3 }}>Fraflytningsopgørelser</div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>Depositum-afregning ved fraflytning — kun depositum kan modregnes, forudbetalt leje vises separat</div>
        </div>
        <Button size="sm" onClick={() => setShowWizard(true)} disabled={contracts.length === 0}>
          <Plus size={13} /> Ny opgørelse
        </Button>
      </div>

      <div style={{
        background: '#fff', borderRadius: 10, border: '1px solid #e8eaed',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderBottom: '1px solid #e8eaed', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 5 }}>
            {[
              { key: '', label: 'Alle statusser' },
              { key: 'draft', label: 'Kladde' },
              { key: 'delivered', label: 'Afleveret' },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setStatusFilter(opt.key)}
                style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 12,
                  fontWeight: statusFilter === opt.key ? 600 : 500,
                  background: statusFilter === opt.key ? '#111827' : '#f7f8fa',
                  color: statusFilter === opt.key ? '#fff' : '#6b7280',
                  border: statusFilter === opt.key ? '1px solid #111827' : '1px solid #e8eaed',
                  cursor: 'pointer', transition: 'all 0.12s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>}
        {error && <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>}

        {!loading && !error && filtered.length === 0 && (
          <div style={{ padding: 64, textAlign: 'center' }}>
            <Receipt size={32} style={{ color: '#d1d5db', marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 4 }}>Ingen fraflytningsopgørelser endnu</div>
            <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16 }}>
              {contracts.length === 0
                ? 'Et lejeforhold skal opsiges først — gå til lejeforholdet og brug "Opsig lejeforhold".'
                : 'Opret den første opgørelse for et opsagt lejeforhold.'}
            </div>
            {contracts.length > 0 && (
              <Button size="sm" onClick={() => setShowWizard(true)}>
                <Plus size={13} /> Ny opgørelse
              </Button>
            )}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fff' }}>
                {['Lejemål', 'Lejer', 'Depositum', 'Forudbetalt', 'Netto', 'Status', 'Fraflytning', ''].map(h => (
                  <th key={h} style={{
                    padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600,
                    color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em',
                    borderBottom: '1px solid #e8eaed', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr key={s.id} style={{ borderBottom: i === filtered.length - 1 ? 'none' : '1px solid #f3f4f6' }}>
                  <td style={{ padding: '11px 14px', fontWeight: 500, color: '#111827' }}>
                    <Link to={`/lejeforhold/${s.contract_id}`} style={{ color: '#4f46e5', textDecoration: 'none' }}>
                      {s.lease_address ?? '—'}
                    </Link>
                  </td>
                  <td style={{ padding: '11px 14px', color: '#6b7280' }}>{s.renter_name ?? '—'}</td>
                  <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 12, color: '#111827' }}>
                    {formatDKK(s.deposit_oere)}
                  </td>
                  <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 12, color: '#9ca3af' }}
                    title="Forudbetalt leje vises separat — ikke en del af depositum-potten">
                    {formatDKK(s.forudbetalt_leje_oere)}
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <OutcomeAmount outcome={s.outcome} netto_oere={s.netto_oere} />
                  </td>
                  <td style={{ padding: '11px 14px' }}>
                    <StatusChip status={s.status} />
                  </td>
                  <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                    {fmtDate(s.fraflytning_dato)}
                  </td>
                  <td style={{ padding: '11px 14px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button
                        onClick={() => downloadPdf(s.id)}
                        title="Hent PDF"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, display: 'inline-flex', alignItems: 'center' }}
                      >
                        <FileDown size={14} />
                      </button>
                      {s.status === 'draft' && (
                        <>
                          <Button
                            size="sm"
                            isLoading={delivering === s.id}
                            disabled={deletingId === s.id}
                            onClick={() => deliverItem(s)}
                            style={{ fontSize: 11, padding: '3px 10px', height: 26 }}
                          >
                            <Send size={11} /> Aflever
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            isLoading={deletingId === s.id}
                            disabled={delivering === s.id}
                            onClick={() => deleteDraft(s)}
                            title="Slet kladde"
                            style={{ fontSize: 11, padding: '3px 10px', height: 26, color: '#b91c1c', whiteSpace: 'nowrap' }}
                          >
                            <Trash2 size={11} /> Slet kladde
                          </Button>
                        </>
                      )}
                      {s.status === 'delivered' && (
                        <Link
                          to={`/lejeforhold/${s.contract_id}`}
                          style={{ color: '#9ca3af', textDecoration: 'none', padding: 4, display: 'inline-flex' }}
                          title="Åbn lejeforhold"
                        >
                          <ExternalLink size={14} />
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showWizard && (
        <CreationWizard
          contracts={contracts}
          initialContractId={initialContractParam}
          onClose={closeWizard}
          onCreated={() => { fetchItems() }}
          onDelivered={(s) => {
            setToast({ message: `Opgørelsen er afleveret (${s.outcome === 'refund' ? 'til godskrivning' : 'efterbetaling'}).` })
            fetchItems()
          }}
        />
      )}

      {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
    </AppLayout>
  )
}
